import { useAwarenessUpdate, useBroadcastUpdate, useMultiuserContext, useSynchronizedState } from "@solidwhite/multiuser";
import { useUsers } from "y-presence";
import Cursor from "./Cursor";

import { animated, useSpring } from "@react-spring/three";
import { useEffect, useMemo, useRef, useState } from "react";

import { useThree } from "@react-three/fiber";
import { throttle } from "lodash-es";
import * as THREE from "three";

const cursorRaycaster = new THREE.Raycaster();
cursorRaycaster.layers.set(2);
const cursorpointer = new THREE.Vector2();

const MultiuserCursors = ({ ...props }) => {
  const { awareness } = useMultiuserContext();

  const users = useUsers(
    awareness,
    (state) =>
      // @ts-ignore
      Array.from(state)
        // @ts-ignore
        // .filter(([id, data]) => id != awareness.clientID)
        // @ts-ignore
        .filter(([id, data]) => !data.presentator && data.name && id != awareness?.clientID)
        // @ts-ignore
        .map(([id, data]) => id),
    (a, b) => {
      return a.length == b.length;
    },
  );

  return (
    <>
      {users.map((id) => (
        <RemoteCursor id={id} key={`Remotecursor_${id}`} />
      ))}
    </>
  );
};

const RemoteCursor = ({ id }) => {
  const { awareness } = useMultiuserContext();

  const position = useRef([0, 0, 0]);

  const { pointing } = useUsers(
    awareness,
    (state) => {
      return state.get(id);
    },
    (a, b) => {
      return a.pointing == b.pointing;
    },
  );

  const { color } = useUsers(
    awareness,
    (state) => {
      return state.get(id);
    },
    (a, b) => {
      return a.color == b.color;
    },
  );

  const { name } = useUsers(
    awareness,
    (state) => {
      return state.get(id);
    },
    (a, b) => {
      return a.name == b.name;
    },
  );

  const [spring, api] = useSpring(() => ({
    pointer: [0, 0, 0],
    config: {
      tension: 280,
      friction: 30,
    },
  }));

  useEffect(() => {
    if (pointing) {
      api.set({ pointer: position.current });
    }
  }, [pointing]);

  useBroadcastUpdate(id, "pointer", (e) => {
    if (!e) return;
    api.start({ pointer: e });
    position.current = e;
  });

  return (
    <>
      <animated.group position={spring.pointer}>
        <Cursor color={color ? color : "#f5a742"} show={pointing} text={name} />
        {/* <TestCursor show={pointing} /> */}
      </animated.group>
    </>
  );
};

export const OwnCursor = () => {
  const { camera, scene } = useThree();
  const { awareness, broadcastMessage } = useMultiuserContext();
  const [pointing, setPointing] = useState(false);
  const pointingRef = useRef(false);

  const [enabledCursors, _] = useSynchronizedState("enable-cursors", true);

  const id = useMemo(() => {
    return awareness?.clientID;
  }, [awareness]);

  const { color } = useUsers(
    awareness,
    (state) => {
      return state.get(id);
    },
    (a, b) => {
      return a.color == b.color;
    },
  );

  const { name } = useUsers(
    awareness,
    (state) => {
      return state.get(id);
    },
    (a, b) => {
      return a.name == b.name;
    },
  );

  const throttledSend = useMemo(() => {
    return throttle(() => {
      cursorRaycaster.setFromCamera(cursorpointer, camera);
      var hit = cursorRaycaster.intersectObject(scene, true);

      broadcastMessage("pointer", hit.length > 0 ? hit[0].point.toArray() : [0, 0, 0]);
    }, 64);
  }, []);

  useEffect(() => {
    // if (!pointing) awareness?.setLocalStateField("pointing", pointing);
    awareness?.setLocalStateField("pointing", pointingRef.current);
  }, [pointing]);

  const handleKeyDown = (e) => {
    // console.log(e);

    setPointing(true);
    // awareness?.setLocalStateField("pointing", true);
    pointingRef.current = true;
    onPointerMove(e);
    if (e.code != "Space") return;
  };

  const handleKeyUp = (e) => {
    pointingRef.current = false;
    setPointing(false);
  };

  useEffect(() => {
    if (enabledCursors) {
      document.addEventListener("mousedown", handleKeyDown);
      document.addEventListener("mouseup", handleKeyUp);
      window.addEventListener("blur", handleKeyUp);
    } else {
      document.removeEventListener("mousedown", handleKeyDown);
      document.removeEventListener("mouseup", handleKeyUp);
      window.removeEventListener("blur", handleKeyUp);
    }

    return () => {
      document.removeEventListener("mousedown", handleKeyDown);
      document.removeEventListener("mouseup", handleKeyUp);
      window.removeEventListener("blur", handleKeyUp);
    };
  }, [enabledCursors]);

  const onPointerMove = (event) => {
    // calculate pointer position in normalized device coordinates
    // (-1 to +1) for both components

    cursorpointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    cursorpointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
    cursorRaycaster.setFromCamera(cursorpointer, camera);
    var hit = cursorRaycaster.intersectObject(scene, true);

    let target = hit.length > 0 ? hit[0].point : lastHit;

    api.start({
      position: target.toArray(),
    });
    if (pointingRef.current) {
      throttledSend();
    }
    // throttledSend();
  };

  useEffect(() => {
    if (enabledCursors) {
      window.addEventListener("pointermove", onPointerMove);
    } else {
      window.removeEventListener("pointermove", onPointerMove);
    }
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
    };
  }, [enabledCursors]);

  useEffect(() => {
    if (!enabledCursors) {
      setPointing(false);
      pointingRef.current = false;
    }
  }, [enabledCursors]);

  const [spring, api] = useSpring(() => ({
    position: [0, 0, 0],
    config: {
      duration: 16,
      tension: 380,
      friction: 30,
    },
  }));

  return (
    <animated.group position={spring.position} scale={1}>
      <Cursor show={pointing} color={color} text={name} />
    </animated.group>
  );
};

const TestCursor = ({ show }) => {
  return (
    <>
      <mesh>
        <boxGeometry />
        <meshBasicMaterial color={show ? "green" : "red"} />
      </mesh>
    </>
  );
};

export default MultiuserCursors;
